import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  BarChart3, Package, Building2, TrendingUp, Users, RefreshCw, Download, FileText,
  Calendar, AlertCircle, ArrowUpRight, ArrowDownRight, Sparkles, Cpu,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend,
} from "recharts";
import { endpoints, API } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import axios from "axios";

const SOURCE_META = {
  definite: { label: "Definite · HRMS",   color: "#0F9B7F", desc: "New-hire signals" },
  high:     { label: "High · Warranty",   color: "#0033A0", desc: "Warranty expiry" },
  medium:   { label: "Medium · Refresh",  color: "#F59E0B", desc: "≥ 36-month age" },
  low:      { label: "Low · Discretionary","color": "#DA291C", desc: "Low Device UX + transfers" },
};

function Kpi({ label, value, sub, icon: Icon, accent, delta }) {
  return (
    <Card
      data-testid={`forecast-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className={`p-5 border-slate-200 shadow-sm ${accent ? "bg-[#0033A0] text-white border-transparent" : ""}`}
    >
      <div className={`flex items-center justify-between ${accent ? "text-white/70" : "text-slate-500"}`}>
        <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
        <Icon size={14} />
      </div>
      <div className={`font-display font-black text-3xl mt-1 ${accent ? "text-white" : "text-slate-900"}`}>{value}</div>
      {sub && (
        <div className={`text-xs mt-1 flex items-center gap-1 ${accent ? "text-white/80" : "text-slate-500"}`}>
          {delta != null && (
            <span className={`inline-flex items-center gap-0.5 font-semibold ${delta >= 0 ? "text-[#0F9B7F]" : "text-[#DA291C]"} ${accent ? "!text-white" : ""}`}>
              {delta >= 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
              {Math.abs(delta)}%
            </span>
          )}
          {sub}
        </div>
      )}
    </Card>
  );
}

function ConfidenceBar({ confidence, total }) {
  const buckets = ["definite", "high", "medium", "low"];
  return (
    <div>
      <div className="flex h-3 rounded-md overflow-hidden bg-slate-100">
        {buckets.map((b) => {
          const w = total > 0 ? (confidence[b] / total) * 100 : 0;
          return <div key={b} style={{ width: `${w}%`, backgroundColor: SOURCE_META[b].color }} title={`${SOURCE_META[b].label}: ${confidence[b]}`} />;
        })}
      </div>
      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
        {buckets.map((b) => (
          <div key={b} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: SOURCE_META[b].color }} />
            <div className="min-w-0">
              <div className="font-semibold text-slate-900">{confidence[b]} <span className="text-slate-400 font-normal">devices</span></div>
              <div className="text-slate-500 truncate">{SOURCE_META[b].label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function fmtUsd(v) {
  return `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function AdminForecast() {
  const { brand } = useBranding();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [ccFilter, setCcFilter] = useState("all");
  const [aribaOpen, setAribaOpen] = useState(false);
  const [aribaData, setAribaData] = useState(null);
  const [aribaLoading, setAribaLoading] = useState(false);

  const load = async () => {
    try {
      setRefreshing(true);
      const d = await axios.get(`${API}/forecast/next-quarter`).then((r) => r.data);
      setData(d);
    } catch (e) {
      toast.error("Could not load forecast");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredLines = useMemo(() => {
    if (!data) return [];
    if (ccFilter === "all") return data.line_items;
    return data.line_items.filter((r) => r.cost_center === ccFilter);
  }, [data, ccFilter]);

  const openAriba = async () => {
    setAribaLoading(true);
    try {
      const d = await axios.get(`${API}/forecast/next-quarter/ariba`).then((r) => r.data);
      setAribaData(d);
      setAribaOpen(true);
    } catch { toast.error("Ariba PR draft could not be generated"); }
    finally { setAribaLoading(false); }
  };

  const downloadCsv = () => {
    window.open(`${API}/forecast/next-quarter/csv`, "_blank");
    toast.success("Forecast CSV downloaded");
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-10 w-96" />
          <div className="grid grid-cols-4 gap-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}</div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  const { kpis, confidence, by_cost_center, by_category, monthly_demand, hrms_events, cost_centers } = data;
  const totalDevices = kpis.total_devices;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-forecast-page">
      {/* Header stripe */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#0033A0] grid place-items-center">
              <BarChart3 size={18} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{brand.small} · Flexspace · Admin</div>
              <div className="font-display font-bold text-slate-900 text-lg leading-tight">
                Forecasting Console · {data.quarter_label}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600">Admin view · unlinked</Badge>
            <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="forecast-refresh-btn" className="border-slate-300">
              <RefreshCw size={13} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="forecast-csv-btn" className="border-slate-300">
              <Download size={13} className="mr-1.5" /> CSV
            </Button>
            <Button size="sm" onClick={openAriba} disabled={aribaLoading} data-testid="forecast-ariba-btn" className="bg-[#0033A0] hover:bg-[#002573] text-white">
              <FileText size={13} className="mr-1.5" /> Auto-generate Ariba PR
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8 space-y-8">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Forecast Total"   value={fmtUsd(kpis.total_amount)} icon={TrendingUp} accent
               delta={kpis.delta_pct} sub={`vs ${fmtUsd(kpis.prev_quarter_amount)} prev qtr`} />
          <Kpi label="Devices Needed"   value={kpis.total_devices} icon={Package} sub={`Across ${kpis.line_items} line items`} />
          <Kpi label="Cost Centers"     value={kpis.cost_centers} icon={Building2} sub={`${brand.orgName} BUs impacted`} />
          <Kpi label="Signals Blended"  value="4" icon={Sparkles} sub="HRMS · Warranty · Refresh · Device UX" />
        </div>

        {/* Confidence + Monthly demand */}
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
          <Card className="p-6 border-slate-200 shadow-sm" data-testid="confidence-panel">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              <AlertCircle size={12} className="text-[#0033A0]" /> Signal confidence
            </div>
            <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Where the demand comes from</h2>
            <p className="text-xs text-slate-500 mt-1">Every forecasted device is traced back to a source signal for auditability.</p>
            <div className="mt-4">
              <ConfidenceBar confidence={confidence} total={totalDevices} />
            </div>
            <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className="flex items-center gap-2"><Users size={12} className="text-[#0F9B7F]" /><span className="text-slate-600">{data.hrms_events.filter((e) => e.type === "New Hire").reduce((s, e) => s + e.count, 0)} new hires</span></div>
              <div className="flex items-center gap-2"><FileText size={12} className="text-[#0033A0]" /><span className="text-slate-600">{data.warranty_expirations.reduce((s, w) => s + w.count, 0)} warranties expiring</span></div>
              <div className="flex items-center gap-2"><RefreshCw size={12} className="text-[#F59E0B]" /><span className="text-slate-600">{data.refresh_overdue_count} refresh overdue</span></div>
              <div className="flex items-center gap-2"><Cpu size={12} className="text-[#DA291C]" /><span className="text-slate-600">{data.low_dex_count} low Device UX flagged</span></div>
            </div>
          </Card>

          <Card className="p-6 border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              <Calendar size={12} className="text-[#0033A0]" /> Monthly demand
            </div>
            <h2 className="font-display font-bold text-xl text-slate-900 mt-1">When to place PRs</h2>
            <div className="h-52 mt-3" data-testid="monthly-demand-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly_demand} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
                  <Bar dataKey="count" fill="#0033A0" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Cost center + category rollup */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="p-6 border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              <Building2 size={12} className="text-[#0033A0]" /> By cost center
            </div>
            <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Where the spend lands</h2>
            <div className="mt-4 border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm" data-testid="by-cost-center-table">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Cost Center</th>
                    <th className="text-left px-4 py-2 font-semibold">Persona</th>
                    <th className="text-right px-4 py-2 font-semibold">Qty</th>
                    <th className="text-right px-4 py-2 font-semibold">Total</th>
                    <th className="text-right px-4 py-2 font-semibold">Budget util.</th>
                  </tr>
                </thead>
                <tbody>
                  {by_cost_center.map((r) => (
                    <tr key={r.cost_center} className="border-t border-slate-200 hover:bg-slate-50">
                      <td className="px-4 py-2 text-xs">
                        <div className="font-mono font-bold text-[#0033A0]">{r.cost_center}</div>
                        <div className="text-slate-500">{r.label}</div>
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-700 capitalize">{r.persona.replace("_", " ")}</td>
                      <td className="px-4 py-2 text-right font-mono">{r.qty}</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900">{fmtUsd(r.amount)}</td>
                      <td className="px-4 py-2 text-right">
                        <span className={`text-xs font-semibold ${r.utilization > 100 ? "text-[#DA291C]" : r.utilization > 80 ? "text-[#F59E0B]" : "text-[#0F9B7F]"}`}>
                          {r.utilization}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="p-6 border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              <Package size={12} className="text-[#0033A0]" /> By category
            </div>
            <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Device mix</h2>
            <div className="h-64 mt-3" data-testid="by-category-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={by_category} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} formatter={(v, k) => k === "amount" ? fmtUsd(v) : v} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="qty" fill="#0033A0" name="Quantity" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="amount" fill="#DA291C" name="Amount ($)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Detailed line items */}
        <Card className="p-6 border-slate-200 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                <BarChart3 size={12} className="text-[#0033A0]" /> Line items
              </div>
              <h2 className="font-display font-bold text-xl text-slate-900 mt-1">The full forecast · {filteredLines.length} rows</h2>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setCcFilter("all")}
                data-testid="cc-filter-all"
                className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${ccFilter === "all" ? "bg-[#0033A0] text-white border-[#0033A0]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              >
                All
              </button>
              {cost_centers.map((c) => (
                <button
                  key={c.id}
                  data-testid={`cc-filter-${c.id}`}
                  onClick={() => setCcFilter(c.id)}
                  className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors font-mono ${ccFilter === c.id ? "bg-[#0033A0] text-white border-[#0033A0]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                >
                  {c.id}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4 border border-slate-200 rounded-md overflow-hidden overflow-x-auto">
            <table className="w-full text-sm" data-testid="forecast-line-items-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Cost Center</th>
                  <th className="text-left px-3 py-2 font-semibold">Persona</th>
                  <th className="text-left px-3 py-2 font-semibold">Category</th>
                  <th className="text-right px-3 py-2 font-semibold">Qty</th>
                  <th className="text-right px-3 py-2 font-semibold">Unit</th>
                  <th className="text-right px-3 py-2 font-semibold">Total</th>
                  <th className="text-left px-3 py-2 font-semibold">Sources</th>
                </tr>
              </thead>
              <tbody>
                {filteredLines.map((r) => (
                  <tr key={`${r.cost_center}-${r.persona}-${r.category}`} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs text-[#0033A0]">{r.cost_center}</td>
                    <td className="px-3 py-2 text-xs text-slate-700 capitalize">{r.persona.replace("_", " ")}</td>
                    <td className="px-3 py-2 text-xs text-slate-900">{r.category_label}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.qty}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-600 text-xs">{fmtUsd(r.avg_unit_price)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900">{fmtUsd(r.amount)}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        {Object.entries(r.sources).map(([k, v]) => v > 0 ? (
                          <span
                            key={k}
                            title={`${SOURCE_META[k].label}: ${v}`}
                            className="text-[9px] font-mono px-1.5 py-0.5 rounded text-white"
                            style={{ backgroundColor: SOURCE_META[k].color }}
                          >
                            {k[0].toUpperCase()}{v}
                          </span>
                        ) : null)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="text-[10px] text-slate-400 text-center pb-6">
          {brand.footer} · Flexspace Forecasting · This page is unlinked from the main navigation.
        </div>
      </div>

      {/* Ariba PR Draft Dialog */}
      <Dialog open={aribaOpen} onOpenChange={setAribaOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] bg-white overflow-hidden flex flex-col" data-testid="ariba-pr-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-lg">Ariba PR draft · {aribaData?.requisition_id}</DialogTitle>
            <DialogDescription className="text-xs">
              Ready-to-punchout requisition · <span className="font-mono">POST /Requisitions</span> · total {fmtUsd(aribaData?.total_amount || 0)}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-auto flex-1 mt-2">
            <pre className="text-[11px] font-mono bg-slate-900 text-slate-100 rounded-md p-3 overflow-auto">
              {aribaData ? JSON.stringify(aribaData, null, 2) : ""}
            </pre>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { navigator.clipboard.writeText(JSON.stringify(aribaData, null, 2)); toast.success("PR JSON copied"); }}
            >
              Copy JSON
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
